/**
 * Team Spain v0 — Tipos de Dominio M6: Formularios, Seguimiento y Salud Básica
 * 
 * Reglas de diseño y privacidad:
 * - Separación estricta entre disponibilidad operativa (pública para staff) y notas clínicas (solo medic).
 * - Carga deportiva sRPE = minutos * RPE (accesible por coach y preparador).
 * - Escalas de bienestar con etiquetas explícitas sin inversión arbitraria.
 * - Respuestas estructuradas versionadas y obligatoriedad acotada al roster.
 */

export type ModalityType = 'flag' | 'tackle' | 'both'

export type ActivityType = 'flag' | 'tackle' | 'strength' | 'competition' | 'other'

export type ActivityOrigin = 'club' | 'selection' | 'own'

export type BodyZone =
  | 'head'
  | 'neck'
  | 'shoulder'
  | 'arm'
  | 'elbow'
  | 'wrist_hand'
  | 'chest'
  | 'upper_back'
  | 'lower_back'
  | 'hip_groin'
  | 'thigh_quad'
  | 'thigh_hamstring'
  | 'knee'
  | 'lower_leg_shin'
  | 'calf'
  | 'ankle'
  | 'foot_toes'

export type Laterality = 'left' | 'right' | 'bilateral' | 'central' | 'na'

export type DeclaredAvailability = 'available_100' | 'limited' | 'needs_assessment' | 'unavailable'

export type OperationalStatus = 'available' | 'limited' | 'needs_assessment' | 'unavailable'

export type ActivityEffect =
  | 'none'
  | 'normal_with_pain'
  | 'modified'
  | 'missed_sessions'
  | 'did_not_train'

export type FormObligationStatus = 'pending' | 'draft' | 'overdue' | 'submitted'

export interface FormObligation {
  id?: string
  form_type: 'profile' | 'weekly' | 'incident' | 'camp_daily'
  title: string
  description: string
  priority: number
  status: FormObligationStatus
  period_start: string | null
  period_end: string | null
  open_at: string | null
  due_at: string | null
  is_late: boolean
  can_submit: boolean
  response_id?: string | null
  event_id?: string | null
  event_title?: string | null
  daily_checkin_due_time?: string | null
}

export interface TrainingLogItem {
  id: string
  athlete_id: string
  team_id: string
  session_date: string
  session_time?: string | null
  activity_type: ActivityType
  other_activity_description?: string | null
  duration_minutes: number
  rpe: number
  session_load: number
  origin: ActivityOrigin
  sports_comment?: string | null
  competition_discipline?: 'flag' | 'tackle' | null
  competition_matches_count?: number
  competition_tournament?: string | null
  training_session_id?: string | null
  confirmed_participation: boolean
  created_at: string
  updated_at: string
}

export interface DiscomfortEntry {
  zone: BodyZone
  side: Laterality
  pain_level: number // 0-10
  is_new: boolean
  episode_id?: string
  description?: string
}

export type NutritionQuality = 1 | 2 | 3 | 4 | 5
export type WaterIntake = 'lt_1l' | '1_to_2l' | '2_to_3l' | 'gt_3l'
export type ProteinFrequency = 'none_or_rare' | 'some' | 'most' | 'all_or_almost'
export type FruitVegServings = '0_to_1' | '2_to_3' | '4_to_5' | 'gt_5'
export type PostWorkoutIntake = 'never_or_rare' | 'sometimes' | 'almost_always' | 'always' | 'not_trained'

export const NUTRITION_QUALITY_LABELS: Record<NutritionQuality, string> = {
  1: '1 — Muy mala',
  2: '2 — Mala',
  3: '3 — Regular',
  4: '4 — Buena',
  5: '5 — Muy buena',
}

export const WATER_INTAKE_LABELS: Record<WaterIntake, string> = {
  lt_1l: 'Menos de 1 litro',
  '1_to_2l': 'De 1 a menos de 2 litros',
  '2_to_3l': 'De 2 a 3 litros',
  gt_3l: 'Más de 3 litros',
}

export const PROTEIN_FREQUENCY_LABELS: Record<ProteinFrequency, string> = {
  none_or_rare: 'En ninguna o casi ninguna',
  some: 'En algunas',
  most: 'En la mayoría',
  all_or_almost: 'En todas o casi todas',
}

export const FRUIT_VEG_SERVINGS_LABELS: Record<FruitVegServings, string> = {
  '0_to_1': '0–1',
  '2_to_3': '2–3',
  '4_to_5': '4–5',
  gt_5: 'Más de 5',
}

export const POST_WORKOUT_INTAKE_LABELS: Record<PostWorkoutIntake, string> = {
  never_or_rare: 'Nunca o casi nunca',
  sometimes: 'Algunas veces',
  almost_always: 'Casi siempre',
  always: 'Siempre',
  not_trained: 'No he entrenado ni competido',
}

export interface WeeklyFormPayload {
  fatigue: number // 1 nada - 5 extrema
  muscle_soreness: number // 1 ninguno - 5 extremo
  recovery: number // 1 muy mala - 5 muy buena
  stress: number // 1 muy bajo - 5 muy alto
  mood: number // 1 muy malo - 5 muy bueno
  sleep_hours: number // 0-24 con 1 decimal
  sleep_quality: number // 1 muy mala - 5 muy buena
  wake_feelings: string[] // 'somnolencia'|'falta_energia'|'no_descanso'|'mal_humor'|'dolor_cabeza'|'apatia'|'none'
  has_discomfort: boolean
  discomfort_areas?: DiscomfortEntry[]
  activity_impact?: ActivityEffect
  impact_explanation?: string
  performance_impact?: number // 1 nada - 5 mucho
  has_illness: boolean
  illness_description?: string
  illness_days_missed?: number // 0-7
  has_professional_care: boolean
  professional_care_type?: string
  professional_care_reason?: string
  professional_care_limitation?: string
  declared_availability: DeclaredAvailability
  contact_requested: boolean
  contact_comment?: string
  draft_saved_at?: string

  // Bloque de Alimentación e Hidratación (v2)
  nutrition_quality?: NutritionQuality
  water_intake?: WaterIntake
  protein_frequency?: ProteinFrequency
  fruit_veg_servings?: FruitVegServings
  post_workout_protein_carb?: PostWorkoutIntake
}

export type DiscomfortStatus = 'none' | 'new' | 'worsened'

export interface CampDailyCheckinPayload {
  sleep_hours: number // 0-24 con decimal (ej. 7.5)
  sleep_quality: number // 1 a 5 (1 Muy mala - 5 Muy buena)
  fatigue: number // 1 a 5 (1 Nada - 5 Extrema)
  recovery: number // 1 a 5 (1 Muy mala - 5 Muy buena)
  discomfort_status: DiscomfortStatus
  discomfort_episode_id?: string | null
  related_incident_id?: string | null
  worsened_incident_id?: string | null
  discomfort_notes?: string | null
  contact_requested: boolean
  contact_comment?: string | null
  draft_saved_at?: string
  camp_id?: string
}

export const SLEEP_QUALITY_LABELS: Record<number, string> = {
  1: '1 — Muy mala',
  2: '2 — Mala',
  3: '3 — Regular',
  4: '4 — Buena',
  5: '5 — Muy buena',
}

export const FATIGUE_LABELS: Record<number, string> = {
  1: '1 — Nada de fatiga (óptimo)',
  2: '2 — Poca fatiga',
  3: '3 — Fatiga moderada',
  4: '4 — Fatiga alta',
  5: '5 — Fatiga extrema',
}

export const RECOVERY_LABELS: Record<number, string> = {
  1: '1 — Muy mala',
  2: '2 — Mala',
  3: '3 — Regular',
  4: '4 — Buena',
  5: '5 — Muy buena (óptima)',
}

export const DISCOMFORT_STATUS_LABELS: Record<DiscomfortStatus, string> = {
  none: 'No, sin molestias',
  new: 'Sí, molestia nueva',
  worsened: 'Sí, ha empeorado una existente',
}

export interface FormResponseItem {
  id: string
  team_id: string
  athlete_id: string
  event_id?: string | null
  form_type: 'weekly' | 'profile' | 'camp_daily'
  version_id?: string
  period_start: string
  period_end: string
  status: 'draft' | 'submitted'
  is_late: boolean
  submitted_at?: string | null
  submitted_by?: string | null
  revision?: number
  revision_number?: number
  created_at?: string
  updated_at?: string
  payload: WeeklyFormPayload | CampDailyCheckinPayload
}

export type FormResponseRecord = FormResponseItem

export interface CampDailyComplianceItem {
  athlete_id: string
  first_name: string
  last_name: string
  dorsal_number: number | null
  primary_position: string
  unit?: string | null
  attendance_status: string
  compliance_status: 'pending' | 'draft' | 'submitted' | 'late_submitted'
  is_late: boolean
  submitted_at: string | null
  event_id: string
  checkin_date: string
}

export interface CampDailyMedicalInboxItem extends CampDailyComplianceItem {
  response_id: string | null
  revision: number
  sleep_hours: number | null
  sleep_quality: number | null
  fatigue: number | null
  recovery: number | null
  fatigue_level?: number | null
  recovery_level?: number | null
  discomfort_status: 'none' | 'new' | 'worsened' | null
  discomfort_episode_id: string | null
  contact_requested: boolean
  contact_comment: string | null
  contact_request_id: string | null
  contact_request_status: 'pending' | 'in_progress' | 'resolved' | null
  payload?: CampDailyCheckinPayload | null
}

export interface CampDailyTrendItem {
  checkin_date: string
  day_date?: string
  total_convened: number
  submitted_count: number
  total_responses?: number
  pending_count: number
  late_count: number
  contact_requests_count: number
  contact_request_count?: number
  avg_sleep_hours: number | null
  avg_sleep_quality: number | null
  avg_fatigue: number | null
  avg_recovery: number | null
  discomfort_none_count: number
  discomfort_new_count: number
  discomfort_worsened_count: number
  discomfort_count?: number
}

export interface HealthIncidentItem {
  id: string
  title?: string
  episode_id?: string | null
  team_id: string
  athlete_id: string
  athlete_name?: string
  created_by?: string | null
  body_zone: BodyZone
  laterality?: Laterality | null
  incident_date: string
  context?: 'training' | 'competition' | 'other' | null
  pain_level?: number | null
  description?: string | null
  activity_effect?: ActivityEffect | null
  professional_care?: string | null
  declared_availability?: DeclaredAvailability | null
  contact_requested: boolean
  contact_comment?: string | null
  shared_notes?: string | null
  status: 'open' | 'monitoring' | 'resolved'
  is_active: boolean
  created_at: string
  updated_at: string
  clinical_notes?: string | null // solo si medic
  diagnosis?: string | null // solo si medic
  updates_count?: number
}

export interface ClinicalNoteItem {
  id: string
  incident_id: string
  team_id: string
  internal_notes: string
  diagnosis?: string | null
  created_at: string
  updated_at: string
}

export interface AvailabilityAssessmentItem {
  id: string
  team_id: string
  athlete_id: string
  athlete_name?: string
  dorsal_number?: number | null
  assessed_by?: string | null
  assessed_by_name?: string | null
  status: OperationalStatus
  operational_restriction?: string | null
  shared_instructions?: string | null
  valid_from: string
  next_review_date?: string | null
  created_at: string
  updated_at: string
}

export interface ContactRequestItem {
  id: string
  team_id: string
  athlete_id: string
  athlete_name?: string
  dorsal_number?: number | null
  incident_id?: string | null
  form_response_id?: string | null
  status: 'pending' | 'in_progress' | 'resolved'
  priority: 'normal' | 'urgent'
  reason?: string | null
  assigned_to?: string | null
  assigned_to_name?: string | null
  resolution_notes?: string | null
  requested_at: string
  resolved_at?: string | null
  resolved_by?: string | null
}

export interface TeamComplianceItem {
  athlete_id: string
  first_name: string
  last_name: string
  dorsal_number: number | null
  primary_position: string
  unit: string
  compliance_status: 'pending' | 'draft' | 'submitted' | 'late_submitted'
  is_late: boolean
  submitted_at: string | null
  period_start: string
  period_end: string
}

export interface AthleteProfileData {
  id: string
  first_name: string
  last_name: string
  dorsal_number: number | null
  primary_position: string
  secondary_positions: string[]
  birth_date: string | null
  modality: ModalityType
  start_year: number | null
  flag_club: string | null
  tackle_club: string | null
  city: string | null
  country: string
  is_profile_complete: boolean
}

export interface WeeklyLoadAggregate {
  week_start: string
  week_end: string
  total_minutes: number
  total_load: number
  session_count: number
  unique_days: number
  flag_minutes: number
  strength_minutes: number
  other_minutes: number
  flag_load: number
  strength_load: number
  average_rpe: number
}

export interface TeamFormSettings {
  team_id: string
  weekly_open_day: number
  weekly_open_time: string
  weekly_due_day: number
  weekly_due_time: string
  daily_checkin_due_time?: string
  timezone: string
  created_at?: string
  updated_at?: string
}
